import axios from 'axios';
import type { AxiosError } from 'axios';

/**
 * True when a rejection is a request we aborted ourselves (a superseded fetch, or an unmount)
 * rather than a real failure. Callers that log in their own catch use this to tell the two apart;
 * the axiosInstance response interceptor applies the same rule before it surfaces anything.
 */
export function isCancellation(error: unknown): boolean {
  return axios.isCancel(error) || (error as { code?: string } | null)?.code === 'ERR_CANCELED';
}

/**
 * Structure of error responses from the API
 */
export interface ApiErrorResponse {
  message?: string;
  error?: string;
  detail?: string;
  errorMessage?: string;
  statusCode?: number;
}

/**
 * Type guard to check if an error is an AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extract a user-friendly error message from an API error.
 * 
 * Rules:
 * - 4xx client errors: Display the actual message from the API response
 * - 5xx server errors: Display "Internal error" with appended diagnostic info from API
 * - Network/other errors: Display the fallback message
 * 
 * @param error - The error object (typically from a catch block)
 * @param fallbackMessage - Message to display if no API error message is available
 * @returns User-friendly error message
 * 
 * @example
 * ```typescript
 * try {
 *   await api.updateWorkOrder(data);
 * } catch (err) {
 *   const message = getApiErrorMessage(err, 'Failed to update work order.');
 *   toast.add({ severity: 'error', summary: message });
 * }
 * ```
 */
export function getApiErrorMessage(error: unknown, fallbackMessage: string): string {
  // Not an Axios error - return fallback
  if (!isAxiosError(error)) {
    return fallbackMessage;
  }

  const status = error.response?.status;
  const data = error.response?.data as ApiErrorResponse | string | undefined;
  
  // Extract message from various possible response formats
  let serverMessage: string | undefined;
  if (typeof data === 'string') {
    serverMessage = data;
  } else if (data && typeof data === 'object') {
    // `errorMessage` is the LoginResponse shape (login + impersonation endpoints).
    serverMessage = data.message || data.error || data.detail || data.errorMessage;
  }

  // 4xx client errors: show the API's message directly
  // These are typically validation errors, permission errors, etc.
  if (status && status >= 400 && status < 500) {
    return serverMessage || fallbackMessage;
  }

  // 5xx server errors: "Internal error" + diagnostic info
  // This follows the requirement to display "Internal error" for 5xx
  if (status && status >= 500) {
    if (serverMessage) {
      return `Internal error: ${serverMessage}`;
    }
    return fallbackMessage;
  }

  // Network errors or other issues
  return fallbackMessage;
}

/**
 * User-friendly error message for an *auxiliary* API call that may be denied by
 * privilege. A 403 means the user lacks the relevant read permission, so we name
 * the object they cannot view rather than showing a generic failure. Any other
 * status falls back to {@link getApiErrorMessage}.
 *
 * `resourceName` is supplied by the caller (which knows what it was loading), so
 * the message adapts to whatever object the permission is relevant to.
 *
 * @param error - The error object (typically from a catch block)
 * @param resourceName - Human-readable name of the object, e.g. 'Tasks'
 * @param fallbackMessage - Message for non-403 errors
 * @returns User-friendly error message
 *
 * @example
 * ```typescript
 * } catch (err) {
 *   toast.add({ severity: 'error', summary: getPermissionAwareErrorMessage(err, 'Tasks', 'Failed to load task codes.') });
 * }
 * ```
 */
export function getPermissionAwareErrorMessage(
  error: unknown,
  resourceName: string,
  fallbackMessage: string,
): string {
  if (isAxiosError(error) && error.response?.status === 403) {
    return `You do not have permission to view ${resourceName}.`;
  }
  return getApiErrorMessage(error, fallbackMessage);
}
